import React, { useEffect, useState } from 'react'
import StatCard from './StatCard'
import { Icon } from '@iconify/react'
import LoanCalculationCard, { LoanResult } from '../../components/LoanCalculationCard'
import axiosClient from '../../api/axiosClient'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'

const Dashboard = () => {
  const [totalPayable, setTotalPayable] = useState(0);
  const [monthlyDue, setMonthlyDue] = useState(0);
  const [weeklyDue, setWeeklyDue] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [data, setData] = useState<any>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Only run redirect logic after authentication status is known
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    // Redirect "Customer" role users to Profile
    if (user?.role === 'Customer') {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);




  useEffect(() => {
    const fetchData = async () => {
      try {
         setLoading(true);
        const response = await axiosClient.get("/dashboard/summary");
        setData(response.data);
        // console.log(response.data);
      } catch (error) {
        console.error(error);
      }
       finally {
        setLoading(false);
      }

    };
    fetchData();
  }, []);
  // Optionally render nothing (or a spinner) while redirecting
  if (!isAuthenticated || user?.role === 'Customer') {
    return null;
  }

  const onCalculate = (res: LoanResult) => {
    setTotalPayable(res.totalPayable);
    setMonthlyDue(res.monthlyDue);
    setWeeklyDue(res.weeklyDue || 0);
    setTotalInterest(res.totalInterest || 0);
  };
  const currency = (v: number) =>
    v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
 const SkeletonStat: React.FC = () => (
    <div className="bg-white rounded-xl p-4 shadow-sm animate-pulse">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-4 w-full">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-6 bg-gray-200 rounded w-2/3 mt-2" />
        </div>
        <div className="w-12 h-12 rounded-lg bg-gray-200" />
      </div>
    </div>
  );
   const SkeletonResultRow: React.FC = () => (
    <div className="flex justify-between text-md font-medium rounded-md border-2 border-gray-100 bg-white px-3 py-2 shadow-sm animate-pulse">
      <span className="h-4 w-1/3 bg-gray-200 rounded" />
      <span className="h-4 w-24 bg-gray-200 rounded" />
    </div>
  );

  return (
    <div className='pt-3'>
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ">
             {loading ? (
          // show 4 skeletons while loading
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          // actual stat cards
          <>
            <StatCard
              title="Active Loans"
              value={data?.total_loans || 0}
              icon={<Icon icon="octicon:graph-24" width={30} height={30} />}
              bg="bg-white"
              iconbg="bg-green-100"
              iconcolor="text-green-500"
            />
            <StatCard
              title="Active Customers"
              value={data?.total_customers || 0}
              icon={<Icon icon="fluent:person-star-24-regular" width={30} height={30} />}
              bg="bg-white"
              iconbg="bg-blue-100"
              iconcolor="text-blue-500"
            />
            <StatCard
              title="Active Employees"
              value={data?.total_employees || 0}
              icon={<Icon icon="mdi:person-tie" width={30} height={30} />}
              bg="bg-white"
              iconbg="bg-orange-100"
              iconcolor="text-orange-500"
            />
            <StatCard
              title="Today’s Gross Collections"
              value={data?.today_collection || 0}
              icon={<Icon icon="tdesign:money" width={30} height={30} />}
              bg="bg-white"
              iconbg="bg-orange-100"
              iconcolor="text-orange-500"
            />
          </>
        )}
      </div>

      {/* Loan + Result */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white p-6 rounded-2xl shadow-sm">
        <LoanCalculationCard onCalculate={onCalculate} />

        <div className="flex flex-col justify-center md:p-3">
          <h3 className="text-lg font-semibold mb-4">Result</h3>

          <div className="space-y-3 text-gray-700">
             {loading ? (
              <>
                <SkeletonResultRow />
                <SkeletonResultRow />
                <SkeletonResultRow />
                <SkeletonResultRow />
              </>
            ) : (
              <>
            <div className="flex justify-between text-md font-medium text-blue-400 rounded-md border-2 border-blue-200 bg-white px-3 py-2 shadow-sm">
              <span>Total Payable:</span>
              <span className="font-semibold">{currency(totalPayable)}</span>
            </div>

            <div className="flex justify-between text-md font-medium text-green-400 rounded-md border-2 border-green-200 bg-white px-3 py-2 shadow-sm">
              <span>Total Interest:</span>
              <span className="font-semibold">{currency(totalInterest)}</span>
            </div>

            <div className="flex justify-between text-md font-medium text-red-400 rounded-md border-2 border-red-200 bg-white px-3 py-2 shadow-sm">
              <span>Monthly Due:</span>
              <span className="font-semibold">{currency(monthlyDue)}</span>
            </div>

            {/* <div className="flex justify-between text-md font-medium text-purple-400 rounded-md border-2 border-purple-200 bg-white px-3 py-2 shadow-sm">
              <span>Weekly Due:</span>
              <span className="font-semibold">{currency(weeklyDue)}</span>
            </div> */}
             </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard